// ─────────────────────────────────────────────────────────────────────────────
// CustomerEmailService
// ─────────────────────────────────────────────────────────────────────────────
// HTTP service for the customer emails registry (used by the company-profile
// "Extra Emails" section).
// Endpoints: /v1/company-emails (5 routes — list, getById, create,
// update, remove).
// Source discriminator stays server-side: create/update DTOs intentionally
// do NOT carry `source` — backend stamps 'volta' on create and rejects PATCH
// attempts on 'erp' rows with HTTP 404 (Phase 21 D-14, ID-spraying defence).
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { skipErrorToast } from 'src/app/core/http/error-toast.token';
import { APIResponse } from 'src/app/core/models/api-response';
import { environment } from 'src/environments/environment';
import {
  CompanyEmailSource,
  ICompanyEmail,
  ICreateCustomerEmailPayload,
  ICreateCustomerEmailsBulkPayload,
  ICreateLocationEmailPayload,
  ICreateLocationEmailsBulkPayload,
  IStructuredApiError,
  IUpdateCustomerEmailPayload,
  STRUCTURED_ERROR_CODES,
} from '../models/customer-email.models';

@Injectable({
  providedIn: 'root',
})
export class CustomerEmailService {
  constructor(private http: HttpClient) {}

  // ── List ───────────────────────────────────────────────────────────────────

  public list(customerId: number, source?: CompanyEmailSource): Observable<APIResponse<ICompanyEmail>> {
    let params = new HttpParams().set('customer_id', customerId);
    if (source) {
      params = params.set('source', source);
    }
    return this.http.get<APIResponse<ICompanyEmail>>(`${environment.BASE_URL_API}company-emails`, { params });
  }

  // ── Get by id ──────────────────────────────────────────────────────────────

  public getById(id: number): Observable<APIResponse<ICompanyEmail>> {
    return this.http.get<APIResponse<ICompanyEmail>>(`${environment.BASE_URL_API}company-emails/${id}`);
  }

  // ── Create ─────────────────────────────────────────────────────────────────

  public create(payload: ICreateCustomerEmailPayload): Observable<APIResponse<ICompanyEmail>> {
    return this.http
      .post<APIResponse<ICompanyEmail>>(`${environment.BASE_URL_API}company-emails`, payload, { context: skipErrorToast() })
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => this.toStructuredError(error))));
  }

  // ── Bulk create ──────────────────────────────────────────────────────────

  /**
   * Add several addresses to one company's registry in a single request.
   * Idempotent — already-registered addresses are skipped server-side (no 409),
   * so the caller never needs per-row duplicate handling. `data` carries only
   * the newly-created rows; `metadata` summarizes {requested, created,
   * duplicates, invalid}.
   */
  public createBulk(payload: ICreateCustomerEmailsBulkPayload): Observable<APIResponse<ICompanyEmail>> {
    return this.http
      .post<APIResponse<ICompanyEmail>>(`${environment.BASE_URL_API}company-emails/bulk`, payload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => this.toStructuredError(error))));
  }

  // ── Location-scoped variants (/v1/location-emails) ─────────────────────────
  // Same registry, scoped to one location (res.partner) instead of the whole
  // company. Used by the enrich flows when the user targets a Shipping Location.

  /** List a single location's registry (source-filterable) — for pre-toggling. */
  public listByLocation(locationId: number, source?: CompanyEmailSource): Observable<APIResponse<ICompanyEmail>> {
    let params = new HttpParams().set('location_id', locationId);
    if (source) {
      params = params.set('source', source);
    }
    return this.http.get<APIResponse<ICompanyEmail>>(`${environment.BASE_URL_API}location-emails`, { params });
  }

  /** Add one address to a specific location's registry. */
  public createLocation(payload: ICreateLocationEmailPayload): Observable<APIResponse<ICompanyEmail>> {
    return this.http
      .post<APIResponse<ICompanyEmail>>(`${environment.BASE_URL_API}location-emails`, payload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => this.toStructuredError(error))));
  }

  /** Bulk-add addresses to a specific location's registry; idempotent on duplicates. */
  public createLocationBulk(payload: ICreateLocationEmailsBulkPayload): Observable<APIResponse<ICompanyEmail>> {
    return this.http
      .post<APIResponse<ICompanyEmail>>(`${environment.BASE_URL_API}location-emails/bulk`, payload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => this.toStructuredError(error))));
  }

  // ── Update ─────────────────────────────────────────────────────────────────

  public update(id: number, payload: IUpdateCustomerEmailPayload): Observable<APIResponse<ICompanyEmail>> {
    return this.http
      .post<APIResponse<ICompanyEmail>>(`${environment.BASE_URL_API}company-emails/${id}`, payload, { context: skipErrorToast() })
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => this.toStructuredError(error))));
  }

  // ── Remove ─────────────────────────────────────────────────────────────────

  public remove(id: number): Observable<APIResponse<ICompanyEmail>> {
    return this.http
      .delete<APIResponse<ICompanyEmail>>(`${environment.BASE_URL_API}company-emails/${id}`, { context: skipErrorToast() })
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => this.toStructuredError(error))));
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  /**
   * Map an HttpErrorResponse to a typed IStructuredApiError so effects can
   * dispatch a typed error action without re-inspecting raw HTTP shape.
   * - 404 → EMAIL_NOT_FOUND sentinel (row no longer exists, refresh UX)
   * - 409 → DUPLICATE (backend returns 409 + scalar message for duplicates)
   * - everything else → generic message fallback
   */
  private toStructuredError(error: HttpErrorResponse): IStructuredApiError {
    if (error.status === 404) {
      return {
        code: STRUCTURED_ERROR_CODES.EMAIL_NOT_FOUND,
        message: error?.error?.message || 'Email not found',
      };
    }
    if (error.status === 409) {
      return {
        code: STRUCTURED_ERROR_CODES.DUPLICATE,
        message: error?.error?.message || 'This email already exists',
      };
    }
    return {
      message: error?.error?.message || error?.message || 'Server error',
    };
  }
}
